import { ManualConfirmedPair } from '../../types/entry.types';
import { matchesPairTitleSearch } from '../../utils/pairSearch';

/**
 * Pure filtering/sorting logic for the Pairs Bucket page, extracted so the
 * view rules (Active vs Expired split, expired ordering, search matching,
 * group filtering) are unit-testable without rendering anything.
 */

/** Sentinel group-rail filters — real group ids never collide with these.
 *  `BUCKET_ALL_GROUPS` is deliberately NOT named `ALL_GROUPS`: the entry
 *  page's `groupFilter.ts` exports an `ALL_GROUPS` GroupSelection object
 *  and the two must never be confused at an import site. */
export const BUCKET_ALL_GROUPS = '__all__';
export const UNGROUPED = '__ungrouped__';

export type BucketView = 'active' | 'expired' | 'hidden';

type ExpiryFields = Pick<ManualConfirmedPair, 'expiryDate'>;
type SearchFields = Pick<
  ManualConfirmedPair,
  'pairName' | 'kalshiTitle' | 'polymarketTitle'
>;
type GroupFields = Pick<ManualConfirmedPair, 'openerGroupId'>;

/** Parsed expiry epoch-ms, or null when absent/unparseable. */
export function expiryTime(pair: ExpiryFields): number | null {
  if (!pair.expiryDate) return null;
  const t = new Date(pair.expiryDate).getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * A pair is Expired only when it carries a parseable expiryDate strictly in
 * the past. No expiryDate (null/undefined/garbage) = Active, never Expired.
 */
export function isExpired(pair: ExpiryFields, now: number): boolean {
  const t = expiryTime(pair);
  return t !== null && t < now;
}

/**
 * Split into Active vs Expired. Active keeps the input order; Expired is
 * sorted longest-past-expiry FIRST (oldest expiry at the top).
 */
export function splitByExpiry<T extends ExpiryFields>(
  pairs: T[],
  now: number
): { active: T[]; expired: T[] } {
  const active: T[] = [];
  const expired: T[] = [];
  for (const pair of pairs) {
    (isExpired(pair, now) ? expired : active).push(pair);
  }
  expired.sort((a, b) => (expiryTime(a) ?? 0) - (expiryTime(b) ?? 0));
  return { active, expired };
}

/**
 * Case-insensitive substring match across the three title fields a pair can
 * carry. Delegates to the shared matcher (`utils/pairSearch`) — the SAME
 * predicate the EntryPage search uses, so the two contracts cannot drift.
 */
export function matchesSearch(pair: SearchFields, query: string): boolean {
  return matchesPairTitleSearch(pair, query);
}

export function filterBySearch<T extends SearchFields>(
  pairs: T[],
  query: string
): T[] {
  if (!query.trim()) return pairs;
  return pairs.filter(pair => matchesSearch(pair, query));
}

/** `filter` is BUCKET_ALL_GROUPS, UNGROUPED, or a group id. */
export function matchesGroup(pair: GroupFields, filter: string): boolean {
  if (filter === BUCKET_ALL_GROUPS) return true;
  if (filter === UNGROUPED) return pair.openerGroupId == null;
  return pair.openerGroupId === filter;
}

export function filterByGroup<T extends GroupFields>(
  pairs: T[],
  filter: string
): T[] {
  if (filter === BUCKET_ALL_GROUPS) return pairs;
  return pairs.filter(pair => matchesGroup(pair, filter));
}

/**
 * Relative "resolved Xh ago" badge text for a past expiry; null when the
 * pair has no expiry or it is still in the future.
 */
export function expiredAgoLabel(
  pair: ExpiryFields,
  now: number
): string | null {
  const t = expiryTime(pair);
  if (t === null || t >= now) return null;
  const minutes = Math.floor((now - t) / 60_000);
  if (minutes < 60) return `resolved ${Math.max(minutes, 1)}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `resolved ${hours}h ago`;
  return `resolved ${Math.floor(hours / 24)}d ago`;
}
